import type { DifficultyLevel } from "./types";

export const HUNT_SYMBOLS = ["⭐", "🌙", "💫", "🪐", "☀️", "🌈", "⚡", "🛸", "☁️"] as const;

export interface SkyItem {
  id: string;
  isTarget: boolean;
  symbol: string;
  x: number;
  y: number;
}

export interface CountStarsRound {
  targetSymbol: string;
  targetCount: number;
  items: SkyItem[];
}

function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function shuffle<T>(items: T[], rand: () => number): T[] {
  const next = [...items];
  for (let i = next.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [next[i], next[j]] = [next[j]!, next[i]!];
  }
  return next;
}

export function huntTargetCount(difficulty: DifficultyLevel): number {
  switch (difficulty) {
    case "easy":
      return 2;
    case "medium":
      return 3;
    default:
      return 4;
  }
}

function decoyCountForDifficulty(difficulty: DifficultyLevel) {
  switch (difficulty) {
    case "easy":
      return 5;
    case "medium":
      return 7;
    default:
      return 9;
  }
}

export function buildCountStarsRound(
  difficulty: DifficultyLevel,
  roundKey: number,
): CountStarsRound {
  const rand = mulberry32(roundKey * 9973 + difficulty.length * 131);
  const targetCount = huntTargetCount(difficulty);
  const targetSymbol = HUNT_SYMBOLS[Math.floor(rand() * HUNT_SYMBOLS.length)]!;
  const extraTargets = 2;
  const decoyCount = decoyCountForDifficulty(difficulty);
  const total = targetCount + extraTargets + decoyCount;

  const positions: { x: number; y: number }[] = [];
  const minDist = 0.14;

  for (let i = 0; i < total; i++) {
    let placed = false;
    for (let attempt = 0; attempt < 80; attempt++) {
      const x = 0.08 + rand() * 0.84;
      const y = 0.08 + rand() * 0.84;
      const ok = positions.every((p) => Math.hypot(p.x - x, p.y - y) >= minDist);
      if (ok) {
        positions.push({ x, y });
        placed = true;
        break;
      }
    }
    if (!placed) {
      const angle = (i / total) * Math.PI * 2;
      positions.push({
        x: 0.5 + Math.cos(angle) * 0.28,
        y: 0.5 + Math.sin(angle) * 0.28,
      });
    }
  }

  const shuffledPositions = shuffle(positions, rand);
  const targetSlots = new Set(
    shuffle(
      Array.from({ length: total }, (_, i) => i),
      rand,
    ).slice(0, targetCount + extraTargets),
  );

  const decoyPool = shuffle(
    HUNT_SYMBOLS.filter((s) => s !== targetSymbol),
    rand,
  );

  const items = shuffledPositions.map((pos, index) => {
    const isTarget = targetSlots.has(index);
    return {
      id: `${roundKey}-${index}`,
      isTarget,
      symbol: isTarget ? targetSymbol : decoyPool[index % decoyPool.length]!,
      x: pos.x,
      y: pos.y,
    };
  });

  return { targetSymbol, targetCount, items };
}
